import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useFieldLocked } from './FieldLock';

/**
 * Inside a locked FieldLockScope: the text stays, no change is taken, and the
 * box is never natively `disabled` (that greys it).
 */
const LOCKED_TEXTAREA_PROPS = { readOnly: true, disabled: undefined } as const;

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  wrapperClassName?: string;
  /** Show character count when maxLength is set */
  showCount?: boolean;
  /**
   * Grow with the text and shrink back as it is removed, instead of scrolling
   * inside a fixed height. `rows` (and the 80px floor) stay the minimum; the
   * manual resize handle is dropped, since the box already fits what is in it.
   */
  autoResize?: boolean;
}

/**
 * Keeps a textarea as tall as its text: it grows while typing and shrinks back
 * as text is removed, so the whole text always shows and nothing scrolls inside
 * it. `rows` and any min-height stay the floor.
 *
 * Collapse to `auto` first, so it can shrink as well as grow, then take the
 * text's full height (plus the borders, since scrollHeight leaves them out).
 * After every render, because the text can change without a keystroke (a value
 * set by the caller). jsdom lays nothing out (scrollHeight 0), so there it
 * leaves the height alone.
 *
 * The moment of collapse shortens every scrolling ancestor, and a browser
 * clamps their scroll position to fit; typing near the bottom of a scrolled
 * panel would then jump it. Their positions are put back afterwards.
 */
function useFitToText(innerRef: React.RefObject<HTMLTextAreaElement | null>, enabled: boolean) {
  const fitToText = useCallback(() => {
    const el = innerRef.current;
    if (!el || el.scrollHeight === 0) return;
    const scrolled: Array<[Element, number]> = [];
    for (let p = el.parentElement; p; p = p.parentElement) {
      if (p.scrollTop) scrolled.push([p, p.scrollTop]);
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    for (const [p, top] of scrolled) p.scrollTop = top;
  }, [innerRef]);

  useLayoutEffect(() => {
    if (enabled) fitToText();
  });

  // A new width re-wraps the text, so the height it needs changes too.
  useEffect(() => {
    const el = innerRef.current;
    if (!enabled || !el) return;
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      fitToText();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, fitToText, innerRef]);
}

/** One ref for both the caller and the component's own use. */
function useMergedRef(ref: React.ForwardedRef<HTMLTextAreaElement>) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const setRefs = useCallback(
    (el: HTMLTextAreaElement | null) => {
      innerRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
    },
    [ref],
  );
  return [innerRef, setRefs] as const;
}

/**
 * A bare `<textarea>` that grows with its text (useFitToText), for screens that
 * style their own textarea instead of using Textarea. It adds nothing but the
 * fit: no wrapper, label or base styles. Description fields use it (or
 * Textarea `autoResize`) so the whole description always shows.
 */
export const AutoGrowTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => {
    const [innerRef, setRefs] = useMergedRef(ref);
    useFitToText(innerRef, true);
    return <textarea ref={setRefs} className={cn(className, 'resize-none overflow-hidden')} {...props} />;
  },
);

AutoGrowTextarea.displayName = 'AutoGrowTextarea';

/** Length of whatever a textarea `value`/`defaultValue` prop can legally hold. */
function valueLength(value: TextareaProps['value']): number {
  if (typeof value === 'string') return value.length;
  if (typeof value === 'number') return String(value).length;
  if (Array.isArray(value)) return value.join('').length;
  return 0;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      hint,
      required,
      wrapperClassName,
      className,
      id,
      showCount = false,
      autoResize = false,
      maxLength,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref,
  ) => {
    const locked = useFieldLocked();
    const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const errorId = error ? `${inputId}-error` : undefined;
    const hintId = hint ? `${inputId}-hint` : undefined;

    const countEnabled = showCount && maxLength !== undefined;

    // Uncontrolled callers (react-hook-form `register`) never pass `value`, so the
    // count has to come from the element itself rather than from props.
    const [innerRef, setRefs] = useMergedRef(ref);
    const [charCount, setCharCount] = useState(() =>
      valueLength(value !== undefined ? value : defaultValue),
    );

    // Deliberately runs after *every* render: `reset()`/`setValue()` write straight
    // to the DOM node without firing `onChange`, so re-reading the element is the
    // only way the count stays honest for uncontrolled fields.
    useEffect(() => {
      if (!countEnabled) return;
      const length = innerRef.current?.value.length ?? 0;
      setCharCount((prev) => (prev === length ? prev : length));
    });

    useFitToText(innerRef, autoResize);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (countEnabled) setCharCount(event.target.value.length);
      onChange?.(event);
    };

    const atLimit = countEnabled && charCount >= maxLength!;

    return (
      <div className={cn('w-full', wrapperClassName)}>
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
            {label}
            {required && (
              <span className="ml-0.5 text-red-500" aria-hidden="true">
                {' *'}
              </span>
            )}
          </label>
        )}

        <textarea
          ref={setRefs}
          id={inputId}
          required={required}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={locked ? undefined : handleChange}
          aria-invalid={!!error}
          aria-describedby={[errorId, hintId].filter(Boolean).join(' ') || undefined}
          className={cn(
            'form-input-base min-h-[80px]',
            autoResize ? 'resize-none overflow-hidden' : 'resize-y',
            error && 'form-input-error',
            className,
          )}
          {...props}
          {...(locked ? LOCKED_TEXTAREA_PROPS : {})}
        />

        <div className="mt-1.5 flex items-start justify-between gap-2">
          <div>
            {hint && !error && (
              <p id={hintId} className="text-xs text-slate-500">
                {hint}
              </p>
            )}
            {error && (
              <p id={errorId} role="alert" className="text-xs text-red-600">
                {error}
              </p>
            )}
          </div>
          {countEnabled && (
            <p
              aria-live="polite"
              className={cn(
                'text-xs flex-shrink-0 ml-auto',
                atLimit ? 'text-red-600 font-medium' : 'text-slate-400',
              )}
            >
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
